import { PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';
import { MerchantPayoutFlow, PayoutDestinationType } from 'services/merchants/types';

import { PayoutDestinationsDataRow } from '../../../types';
import { PayoutType } from '../types';

interface PayoutSetupStepState {
  type: PayoutType;
  [PayoutType.Fiat]: SetupPayoutStep;
  [PayoutType.Crypto]: SetupPayoutStep;
}

export enum SetupPayoutStep {
  SelectPayoutType = 'selectPayoutType',
  SelectBankAccount = 'selectBankAccount',
  AccountDetails = 'accountDetails',
  Memo = 'memo',
  Schedule = 'schedule',
  WalletDetails = 'walletDetails',
}

const addFiatPayoutSteps: SetupPayoutStep[] = [
  SetupPayoutStep.SelectPayoutType,
  SetupPayoutStep.AccountDetails,
  SetupPayoutStep.Memo,
  SetupPayoutStep.Schedule,
];

const editFiatPayoutSteps: SetupPayoutStep[] = [
  SetupPayoutStep.SelectPayoutType,
  SetupPayoutStep.SelectBankAccount,
  SetupPayoutStep.Memo,
  SetupPayoutStep.Schedule,
];
const editFiatPayoutStepsWithOneOrLessBankAccount: SetupPayoutStep[] = [
  SetupPayoutStep.SelectPayoutType,
  SetupPayoutStep.AccountDetails,
  SetupPayoutStep.Memo,
  SetupPayoutStep.Schedule,
];

const addDirectFiatPayoutSteps: SetupPayoutStep[] = [
  SetupPayoutStep.SelectPayoutType,
  SetupPayoutStep.AccountDetails,
  SetupPayoutStep.Memo,
];
const editDirectFiatPayoutSteps: SetupPayoutStep[] = [SetupPayoutStep.SelectBankAccount, SetupPayoutStep.Memo];
const editDirectFiatPayoutStepsWithOneOrLessBankAccount: SetupPayoutStep[] = [SetupPayoutStep.AccountDetails, SetupPayoutStep.Memo];

const cryptoPayoutSteps: SetupPayoutStep[] = [SetupPayoutStep.SelectPayoutType, SetupPayoutStep.WalletDetails, SetupPayoutStep.Schedule];
const directCryptoPayoutSteps: SetupPayoutStep[] = [SetupPayoutStep.SelectPayoutType, SetupPayoutStep.WalletDetails];

const getBankAccounts = (payoutDestinations: PayoutDestinationsDataRow[]) => {
  return payoutDestinations.filter((destination) => destination.type === PayoutDestinationType.Bank);
};

const getModalStepForDirectFlow = (editing: boolean, payoutDestinations: PayoutDestinationsDataRow[]) => {
  const bankAccounts = getBankAccounts(payoutDestinations);

  let payoutSteps = {
    [PayoutType.Fiat]: addDirectFiatPayoutSteps,
    [PayoutType.Crypto]: directCryptoPayoutSteps,
  };

  if (editing) {
    const hasOneOrLessBankAccounts = bankAccounts.length <= 1;
    payoutSteps = {
      [PayoutType.Fiat]: hasOneOrLessBankAccounts ? editDirectFiatPayoutStepsWithOneOrLessBankAccount : editDirectFiatPayoutSteps,
      [PayoutType.Crypto]: cryptoPayoutSteps,
    };
  }

  return payoutSteps;
};

const getModalSteps = (editing: boolean, merchantPayoutFlow: MerchantPayoutFlow, payoutDestinations: PayoutDestinationsDataRow[]) => {
  const bankAccounts = getBankAccounts(payoutDestinations);

  if (merchantPayoutFlow === MerchantPayoutFlow.DIRECT) {
    return getModalStepForDirectFlow(editing, bankAccounts);
  }

  let payoutSteps = {
    [PayoutType.Fiat]: addFiatPayoutSteps,
    [PayoutType.Crypto]: cryptoPayoutSteps,
  };

  if (editing) {
    const hasOneOrLessBankAccounts = bankAccounts.length <= 1;
    payoutSteps = {
      [PayoutType.Fiat]: hasOneOrLessBankAccounts ? editFiatPayoutStepsWithOneOrLessBankAccount : editFiatPayoutSteps,
      [PayoutType.Crypto]: cryptoPayoutSteps,
    };
  }

  return payoutSteps;
};

const getInitialSteps = (editing: boolean, merchantPayoutFlow: MerchantPayoutFlow, payoutDestinations: PayoutDestinationsDataRow[]) => {
  const bankAccounts = getBankAccounts(payoutDestinations);
  const modalSteps = getModalSteps(editing, merchantPayoutFlow, bankAccounts);
  const initialFiatStep = modalSteps[PayoutType.Fiat][0];
  const initialCryptoStep = modalSteps[PayoutType.Crypto][0];

  return {
    [PayoutType.Fiat]: initialFiatStep,
    [PayoutType.Crypto]: initialCryptoStep,
  };
};

const initialPayoutSetupStepState = (
  editing: boolean,
  merchantPayoutFlow: MerchantPayoutFlow,
  payoutDestinations: PayoutDestinationsDataRow[],
): PayoutSetupStepState => {
  const bankAccounts = getBankAccounts(payoutDestinations);
  const initialStep = getInitialSteps(editing, merchantPayoutFlow, bankAccounts);

  return {
    type: PayoutType.Fiat,
    [PayoutType.Fiat]: initialStep[PayoutType.Fiat],
    [PayoutType.Crypto]: initialStep[PayoutType.Crypto],
  };
};

interface PayoutStepContextType extends PayoutSetupStepState {
  setPayoutSetupStepState: (newPayoutSetupStepState: PayoutSetupStepState | null) => void;
  proceed: () => void;
  goBack: () => void;
  close: () => void;
  canGoBack: () => boolean;
}

const PayoutSetupStepContext = createContext<PayoutStepContextType | undefined>(undefined);

export const usePayoutSetupStep = () => {
  const context = useContext(PayoutSetupStepContext);
  if (!context) {
    throw new Error('usePayoutContext must be used within a PayoutProvider');
  }
  return context;
};

interface PayoutSetupStepProviderProps extends PropsWithChildren {
  editing?: boolean;
  payoutDestinations: PayoutDestinationsDataRow[];
  merchantPayoutFlow: MerchantPayoutFlow;
  onClose: () => void;
}

export const PayoutSetupStepProvider = ({
  editing = false,
  merchantPayoutFlow,
  payoutDestinations,
  onClose,
  children,
}: PayoutSetupStepProviderProps) => {
  const [stepState, setStepState] = useState<PayoutSetupStepState>(
    initialPayoutSetupStepState(editing, merchantPayoutFlow, payoutDestinations),
  );

  const setPayoutSetupStepState = (newPayout: PayoutSetupStepState | null) => {
    setStepState((prevPayout) => ({
      ...prevPayout,
      ...newPayout,
      setPayoutSetupStepState,
    }));
  };

  const close = () => {
    setPayoutSetupStepState(initialPayoutSetupStepState(editing, merchantPayoutFlow, payoutDestinations));
    onClose();
  };

  const canProceed = () => {
    const currentStep = stepState[stepState.type];
    const modalSteps = getModalSteps(editing, merchantPayoutFlow, payoutDestinations)[stepState.type];
    const currentStepIndex = modalSteps.indexOf(currentStep);

    return currentStepIndex + 1 < modalSteps.length;
  };

  const proceed = () => {
    if (!canProceed()) {
      close();
      return;
    }

    const currentStep = stepState[stepState.type];
    const modalSteps = getModalSteps(editing, merchantPayoutFlow, payoutDestinations)[stepState.type];
    const currenStepIndex = modalSteps.indexOf(currentStep);
    const nextStep = modalSteps[currenStepIndex + 1];

    setPayoutSetupStepState({
      ...stepState,
      [stepState.type]: nextStep,
    });
  };

  const canGoBack = () => {
    const currentStep = stepState[stepState.type];
    const modalSteps = getModalSteps(editing, merchantPayoutFlow, payoutDestinations)[stepState.type];
    const currentStepIndex = modalSteps.indexOf(currentStep);

    return currentStepIndex - 1 >= 0;
  };

  const goBack = () => {
    if (!canGoBack()) {
      close();
      return;
    }

    const currentStep = stepState[stepState.type];
    const modalSteps = getModalSteps(editing, merchantPayoutFlow, payoutDestinations)[stepState.type];
    const currenStepIndex = modalSteps.indexOf(currentStep);
    const previousStep = modalSteps[currenStepIndex - 1];

    setPayoutSetupStepState({
      ...stepState,
      [stepState.type]: previousStep,
    });
  };

  const contextValue = useMemo(
    () => ({
      ...stepState,
      proceed,
      goBack,
      close,
      canGoBack,
      canProceed,
      setPayoutSetupStepState,
    }),
    [stepState],
  );

  return <PayoutSetupStepContext.Provider value={contextValue}>{children}</PayoutSetupStepContext.Provider>;
};
